import React from 'react'
import { Button, Flex, Icon, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react'
import { FiCheck, FiChevronDown, FiGlobe } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { normalizeLanguage } from '../../i18n'
import { colors } from '../../utils/colors'
import { rounded } from '../../utils/ui'

const LANGUAGES = [
  { code: 'es', label: 'Español' },
  { code: 'en', label: 'English' }
] as const

type LanguageCode = (typeof LANGUAGES)[number]['code']

/**
 * Header dropdown that switches the dashboard language; i18next persists the choice in localStorage.
 * Language names are shown in their own language, so they are not translated.
 */
const LanguageSwitcher = React.memo(() => {
  const { t, i18n } = useTranslation()

  const currentLang: LanguageCode = normalizeLanguage(i18n.language)

  return (
    <Menu
      placement='bottom-end'
      autoSelect={false}
    >
      <MenuButton
        as={Button}
        id='LanguageSwitcherButton'
        size='sm'
        variant='outline'
        bg={colors.white}
        borderColor={colors.border}
        color={colors.text[2]}
        fontWeight={500}
        rounded={rounded.input}
        px={{
          base: '0.5rem',
          sm: '0.75rem'
        }}
        aria-label={t('language.label')}
        _hover={{ bg: colors.neutro[2] }}
        _active={{ bg: colors.neutro[1] }}
      >
        <Flex
          align='center'
          gap='0.5rem'
        >
          <Icon as={FiGlobe} />
          <Text
            as='span'
            display={{
              base: 'none',
              sm: 'inline'
            }}
          >
            {currentLang.toUpperCase()}
          </Text>
          <Icon
            as={FiChevronDown}
            display={{
              base: 'none',
              sm: 'inline-block'
            }}
          />
        </Flex>
      </MenuButton>
      <MenuList
        minW='180px'
        py='0.25rem'
        fontSize='sm'
        borderColor={colors.border}
        shadow='md'
      >
        {LANGUAGES.map(({ code, label }) => {
          const isSelected = code === currentLang
          return (
            <MenuItem
              key={code}
              id={`LanguageSwitcherOption-${code}`}
              onClick={() => void i18n.changeLanguage(code)}
              display='flex'
              justifyContent='space-between'
              gap='1rem'
              bg={isSelected ? colors.tertiary[3] : colors.white}
              _hover={{ bg: isSelected ? colors.tertiary[3] : colors.neutro[2] }}
              role='menuitemradio'
              aria-checked={isSelected}
            >
              <Text color={colors.text[2]}>{label}</Text>
              {isSelected && (
                <Icon
                  as={FiCheck}
                  color={colors.tertiary[1]}
                />
              )}
            </MenuItem>
          )
        })}
      </MenuList>
    </Menu>
  )
})

export { LanguageSwitcher }
